/**
 * Is this session an admin viewing the app as someone else?
 * (../wicked-maths-laravel/docs/VIEW_AS_USER.md)
 *
 * One answer, one place. `user.viewing_as` is refreshed from the server on
 * every /api/me, so the API decides an impersonation is live -- not something
 * remembered in localStorage -- and this is the only reader of it. The banner,
 * the 401 handler and the billing controls all ask this question and must never
 * disagree about the answer.
 *
 * Deliberately defensive rather than `state.authReducer.data.user.viewing_as`.
 * One caller runs inside a 401 error handler, where the store shape is `{}`
 * once signed out and a throw would replace a handled 401 with an unhandled
 * exception; another is a component whose test store holds only the slice it
 * cares about. "Not viewing as" is the safe answer in both cases: it leaves the
 * ordinary session working and lets the server, which refuses these operations
 * anyway, have the last word.
 *
 * It lives here rather than in utils/viewAsSession.js because request.js needs
 * it and viewAsSession.js imports request.js -- this module imports nothing.
 */
export const isViewingAs = state => {
	try {
		const { user } = state.authReducer.data
		return !!(user && user.viewing_as)
	} catch (e) {
		return false
	}
}

/*
 * WHICH KIND OF ACCOUNT is being viewed -- the two words the server sends in
 * `viewing_as.user_kind`, and nothing else is a word.
 *
 * The server computes this (App\Support\ViewAs::kindOf) because the client
 * cannot: "parent" is a family pivot role, not `users.role`, so no field the SPA
 * already holds can tell a parent's account from a student's. See
 * ../wicked-maths-laravel/docs/VIEW_AS_USER.md.
 */
export const VIEW_AS_KIND_STUDENT = "student"
export const VIEW_AS_KIND_PARENT = "parent"

/**
 * The noun the banner is allowed to use for the account being viewed:
 * `"student"`, `"parent"`, or `null` for "the server did not tell us".
 *
 * Tests for the two known words, exactly as `isDraft`/`isLive` in ./draft.js do,
 * and NEVER derives one kind from the absence of the other. Anything else --
 * absent, null, empty, or a word a later server teaches us -- comes back null,
 * and null means the caller falls back to the generic wording rather than
 * picking a side.
 *
 * ## Why absent FALLS BACK rather than DEFAULTS
 *
 * This client deploys independently of the API. If the SPA ships first, if the
 * API is rolled back, or if a `/api/me` response was cached before the field
 * existed, `user_kind` simply will not be there -- and both ways of guessing are
 * worse than saying less:
 *
 *   default to "student" -- the banner says "this student's account" while she
 *                           is inside a PARENT's account, which is the one place
 *                           the wrong noun does real damage: anything sent from
 *                           there lands as the parent, not the child.
 *   interpolate anyway   -- "recorded against this 's account", which reads as a
 *                           bug and teaches her to ignore the banner.
 *
 * The generic sentence is true of every non-admin account -- child, adult
 * student and parent alike -- so the floor is not a degraded message, it is the
 * message this banner shipped with. Naming the kind is a refinement on top of a
 * sentence that was already correct, and a refinement is the thing that gives
 * way when the data is not there.
 *
 * Takes the whole `viewing_as` block (null-safe) rather than the string, so no
 * caller has to know the field's name; one rename, one edit.
 */
export const viewAsKindOf = viewingAs => {
	const kind = viewingAs ? viewingAs.user_kind : undefined

	if (kind === VIEW_AS_KIND_STUDENT) return VIEW_AS_KIND_STUDENT
	if (kind === VIEW_AS_KIND_PARENT) return VIEW_AS_KIND_PARENT

	return null
}

export default isViewingAs
